import {Card, closeCurrentDialog, openDeleteDialog, openDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {isArray, isString} from 'ramda-adjunct';

import {useDeleteProductMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {DmsDataGrid, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {AddOrEditProductCatalogItem} from './AddOrEditProductCatalogItem';

const PREDEFINED_COLUMNS = ['name'] as const;

export function ProductCatalogGridCard(props: TestIdProps) {
  const [deleteProduct] = useDeleteProductMutation();

  const openAddDialog = () =>
    openDialog(
      <AddOrEditProductCatalogItem
        onCancel={closeCurrentDialog}
        onSuccess={() => {
          refreshDmsDatagrid('product_catalog');
          closeCurrentDialog();
        }}
        data-testid={suffixTestId('addDialog', props)}
      />,
      {title: i18n.t('entity.productCatalog.actions.addProduct'), scrollBehavior: 'outside'}
    );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const name = getColumnData('name')?.value;

    const id = isArray(rowId) ? rowId[0] : rowId;

    if (actionKey === 'edit') {
      openDialog(
        <AddOrEditProductCatalogItem
          productId={id}
          onCancel={closeCurrentDialog}
          onSuccess={() => {
            refreshData();
            closeCurrentDialog();
          }}
          data-testid={suffixTestId('editDialog', props)}
        />,
        {title: i18n.t('entity.productCatalog.actions.editProduct'), scrollBehavior: 'outside'}
      );
    }

    if (actionKey === 'delete') {
      openDeleteDialog({
        name: isString(name) ? name : undefined,
        onConfirm: async () => {
          await deleteProduct({id})
            .unwrap()
            .then(() => refreshData())
            .catch(handleApiError);
        },
      });
    }
  };

  return (
    <>
      <Card
        title={i18n.t('entity.productCatalog.labels.products')}
        actions={[
          {
            type: 'button',
            variant: 'link',
            onClick: openAddDialog,
            leftIcon: 'content/add_circle',
            title: i18n.t('entity.productCatalog.actions.addProduct'),
            'data-testid': suffixTestId('addButton', props),
          },
        ]}
      >
        <Box height={120} padding={4}>
          <DmsDataGrid
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            gridCode="product_catalog"
            emptyState={{
              action: {
                onClick: openAddDialog,
                title: i18n.t('entity.productCatalog.actions.addProduct'),
              },
            }}
            data-testid={suffixTestId('grid', props)}
          />
        </Box>
      </Card>
    </>
  );
}
